/**
 * Notifications - User feedback notifications
 *
 * Displays toast-style notifications for errors, warnings,
 * info messages, and success feedback.
 */

/**
 * Notifications class - manages notification display
 */
export class Notifications {
  constructor() {
    this.container = null;
    this.init();
  }

  /**
   * Initialize the notification container
   * @private
   */
  init() {
    this.container = document.createElement('div');
    this.container.className = 'notifications';
    this.container.setAttribute('role', 'alert');
    this.container.setAttribute('aria-live', 'polite');
    document.body.appendChild(this.container);
  }

  /**
   * Show a notification
   * @param {Object} options - Notification options
   * @param {string} options.message - Message text
   * @param {string} [options.type='info'] - 'error' | 'warning' | 'info' | 'success'
   * @param {number} [options.duration=5000] - Auto-dismiss duration (0 = manual dismiss)
   * @param {Function} [options.action] - Optional action button callback
   * @param {string} [options.actionLabel] - Action button label
   * @returns {HTMLElement} The notification element
   */
  show({ message, type = 'info', duration = 5000, action, actionLabel }) {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;

    const icon = this.getIcon(type);
    notification.innerHTML = `
      <span class="notification-icon">${icon}</span>
      <span class="notification-message">${this.escapeHtml(message)}</span>
      ${action ? `<button class="notification-action" type="button">${actionLabel || 'Erneut'}</button>` : ''}
      <button class="notification-close" type="button" aria-label="Schließen">&times;</button>
    `;

    // Bind close button
    notification.querySelector('.notification-close').addEventListener('click', () => {
      this.dismiss(notification);
    });

    // Bind action button
    if (action) {
      notification.querySelector('.notification-action').addEventListener('click', () => {
        action();
        this.dismiss(notification);
      });
    }

    this.container.appendChild(notification);

    // Auto-dismiss
    if (duration > 0) {
      setTimeout(() => this.dismiss(notification), duration);
    }

    return notification;
  }

  /**
   * Show error notification
   * @param {string} message - Error message
   * @param {Object} [options] - Additional options
   * @returns {HTMLElement}
   */
  error(message, options = {}) {
    return this.show({ message, type: 'error', duration: 0, ...options });
  }

  /**
   * Show warning notification
   * @param {string} message - Warning message
   * @param {Object} [options] - Additional options
   * @returns {HTMLElement}
   */
  warning(message, options = {}) {
    return this.show({ message, type: 'warning', duration: 7000, ...options });
  }

  /**
   * Show info notification
   * @param {string} message - Info message
   * @param {Object} [options] - Additional options
   * @returns {HTMLElement}
   */
  info(message, options = {}) {
    return this.show({ message, type: 'info', duration: 5000, ...options });
  }

  /**
   * Show success notification
   * @param {string} message - Success message
   * @param {Object} [options] - Additional options
   * @returns {HTMLElement}
   */
  success(message, options = {}) {
    return this.show({ message, type: 'success', duration: 3000, ...options });
  }

  /**
   * Dismiss a notification
   * @param {HTMLElement} notification - Notification element to dismiss
   */
  dismiss(notification) {
    if (!notification || !notification.parentNode) {
      return;
    }
    notification.classList.add('notification--leaving');
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 300);
  }

  /**
   * Clear all notifications
   */
  clearAll() {
    this.container.innerHTML = '';
  }

  /**
   * Get icon for notification type
   * @param {string} type - Notification type
   * @returns {string} Icon character
   * @private
   */
  getIcon(type) {
    const icons = {
      error: '⚠',
      warning: '⚡',
      info: 'ℹ',
      success: '✓'
    };
    return icons[type] || icons.info;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped HTML
   * @private
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Destroy the notifications container
   */
  destroy() {
    if (this.container && this.container.parentNode) {
      this.container.remove();
    }
  }
}

// Singleton instance
let instance = null;

/**
 * Get the singleton Notifications instance
 * @returns {Notifications}
 */
export function getNotifications() {
  if (!instance) {
    instance = new Notifications();
  }
  return instance;
}
